import { Badge, Button, Card, Input } from "@nfp-tracker/components";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Search, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { EmptyState } from "../../components/EmptyState";
import { LoadingSkeleton } from "../../components/LoadingSkeleton";
import { getInstructorClientRoute, ROUTES } from "../../constants/routes";
import { useOwnerContext } from "../../hooks/useOwnerContext";
import { trackEvent } from "../../integrations/analytics/client";
import {
  INSTRUCTOR_CLIENT_PAGE_SIZE,
  listInstructorClients,
} from "../../integrations/supabase/instructor";

const SEARCH_DEBOUNCE_MS = 250;

const formatDate = (value: string | null) => {
  if (!value) return "No entries yet";
  return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const InstructorClients = () => {
  const navigate = useNavigate();
  const ownerContext = useOwnerContext().data;
  const actorUserId = ownerContext?.status === "ready" ? ownerContext.actorUserId : undefined;

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  // Non-instructors are redirected by the shell; this only decides whether the roster query is
  // worth firing while the owner context resolves.
  const isInstructor = ownerContext?.status === "ready" && ownerContext.actorRole === "instructor";

  // Same pre-auth handling as every other shell page: the shell itself only gates on role.
  useEffect(() => {
    if (ownerContext?.status === "unauthenticated") {
      navigate(ROUTES.auth, { replace: true });
    } else if (ownerContext?.status === "needs-onboarding") {
      navigate(ROUTES.onboarding, { replace: true });
    }
  }, [ownerContext, navigate]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const clientsQuery = useQuery({
    queryKey: ["instructor-clients", actorUserId, search, page],
    queryFn: () =>
      listInstructorClients({
        search,
        limit: INSTRUCTOR_CLIENT_PAGE_SIZE,
        offset: page * INSTRUCTOR_CLIENT_PAGE_SIZE,
      }),
    enabled: isInstructor && Boolean(actorUserId),
  });

  const totalCount = clientsQuery.data?.totalCount ?? 0;
  const clients = clientsQuery.data?.clients ?? [];
  const pageCount = Math.max(1, Math.ceil(totalCount / INSTRUCTOR_CLIENT_PAGE_SIZE));
  const rangeLabel = useMemo(() => {
    if (!totalCount) return "";
    const from = page * INSTRUCTOR_CLIENT_PAGE_SIZE + 1;
    const to = Math.min(totalCount, from + clients.length - 1);
    return `${from}–${to} of ${totalCount}`;
  }, [page, clients.length, totalCount]);

  return (
    <div className="space-y-4">
      <div className="relative w-full sm:max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search clients by name or email"
          className="pl-9"
          aria-label="Search clients"
        />
      </div>

      {clientsQuery.isLoading || (!clientsQuery.data && !clientsQuery.isError) ? (
        <LoadingSkeleton />
      ) : clients.length === 0 ? (
        <EmptyState
          icon={Users}
          title={search ? "No matching clients" : "No clients yet"}
          description={
            search
              ? "Try a different name or email."
              : "Clients appear here once they share their chart with you from their account."
          }
        />
      ) : (
        <ul className="space-y-2">
          {clients.map((client) => (
            <li key={client.clientUserId}>
              {/* The whole card is the link: opening a chart is the only thing to do with a row.
                  The chart route owns the view log, so this is plain navigation. */}
              <Card className="transition-colors hover:border-primary/60 focus-within:border-primary">
                <Link
                  to={getInstructorClientRoute(client.clientUserId)}
                  onClick={() => trackEvent("instructor_client_opened", { source: "roster" })}
                  className="flex items-center justify-between gap-3 rounded-lg p-4 outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate font-medium" title={client.email ?? undefined}>
                        {client.displayName}
                      </p>
                      {client.goal ? <Badge variant="secondary">{client.goal}</Badge> : null}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Last entry {formatDate(client.lastObservationDate)}
                      {client.methods.length ? ` · ${client.methods.join(", ")}` : ""}
                    </p>
                  </div>
                  <ChevronRight
                    className="h-4 w-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                </Link>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {totalCount > INSTRUCTOR_CLIENT_PAGE_SIZE ? (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">{rangeLabel}</p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pageCount - 1}
              onClick={() => setPage((current) => Math.min(pageCount - 1, current + 1))}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default InstructorClients;
